import { z } from "zod";

export const ROLES = ["super_admin", "manager", "viewer", "accountant", "cashier", "waiter", "kitchen"] as const;
export type Role = (typeof ROLES)[number];
export const ROLE_LABELS: Record<Role, string> = {
  super_admin: "Super Admin", manager: "Manager", viewer: "Management (view only)", accountant: "Accountant", cashier: "Cashier", waiter: "Waiter", kitchen: "Kitchen Staff"
};

export const PERMISSIONS = [
  "staff.manage", "settings.manage", "orders.create", "orders.read", "orders.collect", "orders.void",
  "payments.capture", "payments.refund", "discounts.apply", "complimentary.approve", "promotions.manage",
  "kitchen.read", "kitchen.update", "reports.read", "audit.read", "menu.manage", "tables.manage",
  "customers.read", "customers.manage", "inventory.manage", "expenses.manage", "shifts.manage",
  "receipts.reprint", "printer.manage", "updates.manage", "data.backup",
  // The accounts app: ledger, payroll, tax and accounting settings. Salaries are private, so managers do not get it.
  "accounts.manage",
  /** Clock staff in and out on the POS (the attendance the accountant pays salaries from). */
  "attendance.clock"
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const MANAGER_DENIED: readonly Permission[] = ["staff.manage", "updates.manage", "settings.manage", "data.backup", "accounts.manage"];
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  super_admin: PERMISSIONS,
  manager: PERMISSIONS.filter((permission) => !MANAGER_DENIED.includes(permission)),
  // Owners and partners who only watch the figures: everything can be seen, nothing can be changed.
  viewer: ["orders.read", "reports.read", "audit.read", "customers.read", "kitchen.read"],
  // The accountant reads every money record of the POS and keeps the books in the accounts app; they change nothing in the POS.
  accountant: ["orders.read", "reports.read", "accounts.manage", "expenses.manage"],
  // Cashiers and waiters do not browse the customer list (phone numbers): a returning caller is looked up by number.
  cashier: ["orders.create", "orders.read", "orders.collect", "payments.capture", "discounts.apply", "kitchen.read", "kitchen.update", "shifts.manage", "attendance.clock"],
  waiter: ["orders.create", "orders.read", "kitchen.read", "kitchen.update"],
  kitchen: ["kitchen.read", "kitchen.update"]
};

export function hasPermission(role: Role, permission: Permission, overrides: readonly Permission[] = []): boolean {
  return ROLE_PERMISSIONS[role].includes(permission) || overrides.includes(permission);
}

/**
 * Staff sign in with a short login name. Firebase Authentication needs an email address, so in
 * cloud mode a name without "@" is stored under this internal domain. No mail is ever sent to it.
 */
export const STAFF_LOGIN_DOMAIN = "staff.mangomoons.local";
export const staffAuthEmail = (login: string) => { const name = login.trim().toLowerCase(); return name.includes("@") ? name : `${name}@${STAFF_LOGIN_DOMAIN}`; };
export const staffLoginName = (email: string) => email.endsWith(`@${STAFF_LOGIN_DOMAIN}`) ? email.slice(0, -STAFF_LOGIN_DOMAIN.length - 1) : email;

export const MoneySchema = z.number().int().safe().nonnegative();
export type Money = z.infer<typeof MoneySchema>;

export function addMoney(...amounts: Money[]): Money {
  const total = amounts.reduce((sum, amount) => sum + amount, 0);
  return MoneySchema.parse(total);
}

/** Half-up rounding on exact integers; basis points are 1/100 of a percent. */
export function percentageOf(amount: Money, basisPoints: number): Money {
  if (!Number.isSafeInteger(basisPoints) || basisPoints < 0 || basisPoints > 10_000) throw new Error("Invalid basis points");
  return MoneySchema.parse(Math.round((amount * basisPoints) / 10_000));
}

export function formatLkr(minor: number): string {
  const sign = minor < 0 ? "-" : "";
  const absolute = Math.abs(minor);
  const whole = Math.trunc(absolute / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${whole}.${(absolute % 100).toString().padStart(2, "0")}`;
}

/** Parses a user-entered rupee amount such as "1,250.50" into integer cents without float drift. */
export function parseLkr(text: string): number | undefined {
  const cleaned = text.replace(/[,\s]/g, "");
  const match = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) return undefined;
  return Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
}

export const OrderTypeSchema = z.enum(["takeaway", "call", "dine_in"]);
export type OrderType = z.infer<typeof OrderTypeSchema>;
export const ORDER_PREFIX: Record<OrderType, string> = { takeaway: "T", call: "C", dine_in: "D" };
export const ORDER_TYPE_LABELS: Record<OrderType, string> = { takeaway: "Takeaway", call: "Call order", dine_in: "Dine-in" };

/** Where a customer placed an order themselves: the Android app, or the ordering site in a phone's browser. */
export const OnlineChannelSchema = z.enum(["app", "web"]);
export type OnlineChannel = z.infer<typeof OnlineChannelSchema>;
/** App and web orders are numbered on their own (A-001, W-001), so the counter and kitchen can tell them apart at a glance. */
export const ONLINE_PREFIX: Record<OnlineChannel, string> = { app: "A", web: "W" };
export const ONLINE_CHANNEL_LABELS: Record<OnlineChannel, string> = { app: "App order", web: "Web order" };

export function formatDailyNumber(type: OrderType, sequence: number, channel?: OnlineChannel): string {
  if (!Number.isSafeInteger(sequence) || sequence < 1 || sequence > 999_999) throw new Error("Invalid order sequence");
  return `${channel ? ONLINE_PREFIX[channel] : ORDER_PREFIX[type]}-${sequence.toString().padStart(3, "0")}`;
}

export const BUSINESS_TIMEZONE = "Asia/Colombo";

export function colomboBusinessDate(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit"
  }).format(date);
}

/** Minutes since Colombo midnight and weekday (0 = Sunday), used for schedules and happy hours. */
export function colomboClock(date: Date): { minutes: number; weekday: number } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TIMEZONE, hour: "2-digit", minute: "2-digit", weekday: "short", hourCycle: "h23"
  }).formatToParts(date);
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(value("weekday"));
  return { minutes: Number(value("hour")) * 60 + Number(value("minute")), weekday };
}

export function addDays(businessDate: string, days: number): string {
  const date = new Date(`${businessDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export type OrderPricingLine = { quantity: number; unitPriceMinor: Money; discountMinor: Money };
export function calculateTotals(lines: readonly OrderPricingLine[], taxBasisPoints: number, serviceBasisPoints: number) {
  const grossMinor = addMoney(...lines.map((line) => MoneySchema.parse(line.quantity * line.unitPriceMinor)));
  const discountMinor = addMoney(...lines.map((line) => line.discountMinor));
  if (discountMinor > grossMinor) throw new Error("Discount exceeds gross amount");
  const subtotalMinor = MoneySchema.parse(grossMinor - discountMinor);
  const taxMinor = percentageOf(subtotalMinor, taxBasisPoints);
  const serviceChargeMinor = percentageOf(subtotalMinor, serviceBasisPoints);
  return { grossMinor, discountMinor, subtotalMinor, taxMinor, serviceChargeMinor, totalMinor: addMoney(subtotalMinor, taxMinor, serviceChargeMinor) };
}
